import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";

/**
 * AI-processing consent gate. Participant must give explicit consent before
 * we send the assessment for analysis. Lane-aware: for Quick Repath, submit
 * is `/assessment/submit-quick` and post-consent routes to the snapshot page.
 *
 * Fix cycle 2026-09-24 (Mark's consent-loop bug): the AI-processing policy
 * version MUST be fetched live from the server (never hardcoded) so that:
 *   1. A legacy participant whose stored consent is v1.0 can accept the
 *      current v2.0 version through this UI without a 400 loop.
 *   2. Future policy version bumps do NOT require a frontend redeploy to
 *      keep new consents flowing.
 *   3. The `existing` check and the POST payload always use the same live
 *      version, so we never post a stale version the backend correctly
 *      rejects.
 * The legacy hardcode `AI_PROCESSING_VERSION = "1.1"` was the root cause;   // HARDCODED_POLICY_VERSION_OK: comment reference to the removed constant
 * removed intentionally.
 */
export default function AssessmentConsent() {
  const [checked, setChecked] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [aiVersion, setAiVersion] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);
  const [lane, setLane] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // Fetch the CURRENT ai_processing policy (no ?version param) so
        // `aiVersion` reflects what the backend will accept right now.
        const [p, c, s] = await Promise.all([
          api.get(`/policies/ai_processing`),
          api.get("/me/consents"),
          api.get("/assessment/state"),
        ]);
        setPolicy(p.data);
        const currentVersion = p.data?.version;
        setAiVersion(currentVersion);
        // "Already consented" iff the participant holds a consent record
        // for the CURRENT ai_processing version. A stale v1.0 or v1.1
        // record does NOT satisfy the gate; we need a fresh acceptance.
        const already = (c.data.consents || []).find(
          (r) => r.policy_type === "ai_processing" && r.version === currentVersion,
        );
        setExisting(already || null);
        if (already) setChecked(true);
        setLane(params.get("lane") || s.data.progress?.lane || "complete");
      } catch (err) {
        setError(extractApiError(err, "Could not load consent details."));
      }
    })();
  }, [params]);

  const submit = async () => {
    if (!checked) {
      setError('Please tick the box to consent, or choose "Save my answers and stay here" below.');
      return;
    }
    if (!aiVersion) {
      setError("We're still loading the current consent version. Please retry in a moment.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (!existing) {
        // Post the LIVE current version. If the backend has advanced
        // the policy since page-load, the 400 will surface with the
        // new version string and the user can reload.
        await api.post("/me/consents", {
          policy_type: "ai_processing",
          version: aiVersion,
        });
      }
      if (lane === "quick") {
        await api.post("/assessment/submit-quick", {
          ai_processing_version: aiVersion,
        });
        // Confirmation loop — cheap read, defensive against write-then-read lag
        let confirmed = false;
        for (let attempt = 0; attempt < 3 && !confirmed; attempt++) {
          try {
            const { data } = await api.get("/assessment/state");
            if (data?.progress?.quick_submitted_at) {
              confirmed = true;
              break;
            }
          } catch (_) { /* transient; retry */ }
          await new Promise((r) => setTimeout(r, 250 * (attempt + 1)));
        }
        navigate("/assessment/quick/snapshot", { replace: true });
      } else {
        await api.post("/assessment/submit", {
          ai_processing_version: aiVersion,
        });
        navigate("/assessment/submitted", { replace: true });
      }
    } catch (err) {
      setError(extractApiError(err, "We could not submit your assessment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <p className="text-xs uppercase tracking-wider text-fg-secondary">Consent</p>
        <h1
          data-testid="consent-title"
          className="mt-1 text-3xl sm:text-4xl tracking-tight font-heading font-medium text-fg-primary"
        >
          AI-processing consent
        </h1>
        <p className="mt-3 text-sm text-fg-secondary leading-relaxed">
          Before your answers can be sent for AI-supported analysis, we need
          your explicit consent. You can still <strong className="text-fg-primary">save your answers</strong> without
          consenting — analysis just stays unavailable for your account.
        </p>

        {error && (
          <Alert variant="destructive" className="mt-6" data-testid="consent-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <section
          data-testid="consent-plain-language"
          className="mt-8 rounded-lg border border-border-subtle bg-bg-surface p-6"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-progress-teal/10 text-progress-teal">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <h2 className="text-lg font-heading font-medium text-fg-primary">In plain language</h2>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-fg-secondary leading-relaxed">
            <li>
              <strong className="text-fg-primary">What we will send:</strong>{" "}
              your assessment answers (the responses you wrote in this
              assessment).
            </li>
            <li>
              <strong className="text-fg-primary">To whom:</strong> the
              configured AI provider (currently an OpenAI-class model, via
              our provider-independent layer).
            </li>
            <li>
              <strong className="text-fg-primary">Why:</strong> so the model
              can draft evidence-linked strengths and 3 Lanes of Direction
              for you to review, correct, or reject.
            </li>
            <li>
              <strong className="text-fg-primary">Uncertainty:</strong> AI
              can make mistakes. Every conclusion is a draft you can edit.
            </li>
            <li>
              <strong className="text-fg-primary">Your control:</strong> you
              can withdraw this consent later. Analysis will stop for your
              account from that point forward.
            </li>
          </ul>
        </section>

        {policy && (
          <details data-testid="consent-full-policy" className="mt-4 rounded-lg border border-border-subtle bg-bg-surface p-6">
            <summary className="cursor-pointer text-sm text-fg-primary">
              Read the full AI-processing notice (v{policy.version})
            </summary>
            <div className="mt-4 text-sm text-fg-secondary whitespace-pre-line max-h-80 overflow-y-auto">
              {policy.content}
            </div>
          </details>
        )}

        <label
          className="mt-6 flex items-start gap-3 p-4 border border-border-subtle rounded-md bg-bg-surface cursor-pointer"
          data-testid="consent-checkbox-row"
        >
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(!!v)}
            data-testid="consent-checkbox"
            disabled={!!existing}
            className="mt-0.5"
          />
          <span className="text-sm text-fg-primary leading-relaxed">
            I consent to sending my assessment answers to the configured AI
            provider for analysis, and I understand AI outputs may be wrong.
            {existing && (
              <span className="block text-xs text-fg-secondary mt-1">
                Recorded {new Date(existing.consented_at).toLocaleString()}.
              </span>
            )}
          </span>
        </label>

        <div className="mt-6 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <Link to={`/assessment/review${lane ? `?lane=${lane}` : ""}`}>
            <Button variant="ghost" data-testid="consent-back-btn" className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover">
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Save my answers and stay here
            </Button>
          </Link>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="consent-submit-btn"
            className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover disabled:opacity-50"
          >
            {submitting ? "Submitting…" : (lane === "quick" ? "Consent and generate my snapshot" : "Consent and submit")}
            <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
